"use client";

import { useQuery } from "@tanstack/react-query";
import { GiftCardProvider, useCentraClient, useToken } from "@trydig/centra/client";
import { AdyenProvider, CheckoutProvider, createAdyen } from "@trydig/checkout";
import { type PropsWithChildren, useMemo } from "react";
import { Skeleton } from "@/components/ui/Skeleton";
import { Typography } from "@/components/ui/Typography";
import { env } from "@/env.mjs";
import { useTranslations } from "@/i18n/client";
import { getBaseUrl } from "@/lib/utils/getBaseUrl";
import { InjectGiftCheckoutScript } from "./InjectGiftCheckoutScript";

/**
 * `GiftCardProvider` blocks on its own bootstrap query forever if the store's
 * gift-certificate endpoints aren't enabled in Centra (it only distinguishes
 * loading vs. ready, not "will never be ready") — so probe availability
 * ourselves, with the main cart's client, before mounting it.
 */
const useGiftCardsAvailable = () => {
	const client = useCentraClient();

	return useQuery({
		queryKey: ["giftCertificates", "available"],
		queryFn: async () => {
			const res = await client.GET("/gift-certificates");
			return !res.error;
		},
		retry: false,
	});
};

const GiftCardCheckoutProviders = ({ children }: PropsWithChildren) => {
	// Reads the gift token — `useToken` resolves against the nearest Centra
	// context, which `GiftCardProvider` has already swapped for this subtree.
	const token = useToken();

	const adyenClient = useMemo(
		() =>
			createAdyen({
				centraUrl: env.NEXT_PUBLIC_CENTRA_CHECKOUT,
				pluginUri: "adyen-drop-in",
				paymentReturnPage: new URL(`/giftcard/${token}/success`, getBaseUrl()).href,
				paymentFailedPage: new URL("/giftcard/payment", getBaseUrl()).href,
			}),
		[token],
	);

	return (
		<CheckoutProvider token={token} centraUrl={env.NEXT_PUBLIC_CENTRA_CHECKOUT}>
			<AdyenProvider client={adyenClient}>{children}</AdyenProvider>
			<InjectGiftCheckoutScript />
		</CheckoutProvider>
	);
};

const GiftCardProvidersFallback = () => (
	<div className="flex flex-col gap-grid-section-vertical-md">
		<Skeleton className="h-8 w-56" />
		<Skeleton className="h-64 w-full" />
	</div>
);

/**
 * Wraps the gift card purchase steps (form → address → payment) in an
 * isolated Centra selection, so buying a gift card never touches — or
 * empties — the visitor's normal shopping cart.
 */
export const GiftCardProviders = ({ children }: PropsWithChildren) => {
	const t = useTranslations();
	const { data: available, isLoading } = useGiftCardsAvailable();

	if (isLoading) return <GiftCardProvidersFallback />;

	if (!available) {
		return (
			<Typography variant="body-md-reg">{t("Gift cards aren't available right now.")}</Typography>
		);
	}

	return (
		<GiftCardProvider fallback={<GiftCardProvidersFallback />} syncCountry>
			<GiftCardCheckoutProviders>{children}</GiftCardCheckoutProviders>
		</GiftCardProvider>
	);
};
